import React, { useCallback, useState } from 'react';
import i18n from '../../../i18n';

import { DEFAULT_OPTION, OptionGroup, OptionsMode } from 'forms/AddProductForm/OptionGroup';
import { Icon } from 'components/Icon/Icon';
import { ReactComponent as PlusIcon } from 'assets/icons/main-16/add--16.svg';
import { Text, TextTypography } from 'components/Text/Text';
import { makeStyles } from '@material-ui/core/styles';
import { WrappedFieldProps } from 'redux-form';
import { LargeHorizontalDivider } from 'components/HorizontalDivider/HorizontalDivider';

export interface OptionGroupsControlProps {
    onChange: (groups: OptionGroup[]) => void;
    value?: OptionGroup[];
    isEditable?: boolean;
}

const DEFAULT_OPTION_GROUP: OptionGroup = {
    name: 'onboarding:card.defaultOptionName',
    maxOptions: 1,
    minOptions: 1,
    options: [{ ...DEFAULT_OPTION }],
    optionGroupMode: OptionsMode.ALL,
};

const useStyles = makeStyles(theme => ({
    addOptionGroup: {
        margin: theme.spacing(2),
        '&:last-child': {
            marginBottom: 36,
        },
    },
    addOptionButton: {
        display: 'flex',
        alignItems: 'center',
        flexDirection: 'row',
        cursor: 'pointer',
        marginBottom: theme.spacing(2.5),
    },
    addIcon: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        marginRight: theme.spacing(1),
    },
    line: {
        marginLeft: -theme.spacing(5),
        marginRight: -theme.spacing(5),
        [theme.breakpoints.down(640)]: {
            marginLeft: -theme.spacing(3),
            marginRight: -theme.spacing(3),
        },
        [theme.breakpoints.down(480)]: {
            marginLeft: -theme.spacing(2),
            marginRight: -theme.spacing(2),
        },
    },
}));

export const OptionGroupsControl = (props: OptionGroupsControlProps) => {
    const { t } = i18n.useTranslation();
    const { onChange, isEditable } = props;
    const classes = useStyles();
    const [groups, setGroups] = useState<OptionGroup[]>(props.value || []);

    const handleGroupsChange = useCallback(
        optionGroups => {
            setGroups(optionGroups);
            onChange(optionGroups);
        },
        [setGroups, onChange],
    );

    const handleAddNewGroup = useCallback(() => {
        const optionGroups: OptionGroup[] = [];
        groups.forEach(el => optionGroups.push({ ...el }));
        optionGroups.push({ ...DEFAULT_OPTION_GROUP });
        handleGroupsChange(optionGroups);
    }, [groups, handleGroupsChange]);

    const handleRemoveGroup = useCallback(
        index => {
            const optionGroups = groups.filter((el, i) => i !== index);
            handleGroupsChange(optionGroups);
        },
        [groups, handleGroupsChange],
    );

    const handleGroupChange = useCallback(
        (optionGroup: OptionGroup, index: number) => {
            const optionGroups: OptionGroup[] = [];
            groups.forEach(el => optionGroups.push({ ...el }));
            optionGroups[index] = { ...optionGroup };
            handleGroupsChange(optionGroups);
        },
        [groups, handleGroupsChange],
    );

    return (
        <div>
            {groups?.map((group, index) => (
                <>
                    <OptionGroup
                        key={index}
                        index={index}
                        optionGroup={group}
                        isEditable={isEditable}
                        onChange={optionGroup => handleGroupChange(optionGroup, index)}
                        onRemove={() => handleRemoveGroup(index)}
                    />
                    <div className={classes.line}>
                        <LargeHorizontalDivider />
                    </div>
                </>
            ))}
            <div className={classes.addOptionGroup}>
                <div onClick={() => handleAddNewGroup()} className={classes.addOptionButton}>
                    <div className={classes.addIcon}>
                        <Icon component={PlusIcon} size={16} svgSize={16} />
                    </div>
                    <Text typography={TextTypography.PARA_ACCENT}>{t('onboarding:card.addOptionsGroup')}</Text>
                </div>
                <div>
                    <Text typography={TextTypography.CAPTION}>{t('onboarding:card.optionsGroupDesc')}</Text>
                </div>
            </div>
        </div>
    );
};

export type OptionGroupsControlFieldProps = OptionGroupsControlProps & WrappedFieldProps;

export const OptionGroupsControlField = (props: OptionGroupsControlFieldProps) => {
    const {
        input,
        isEditable,
        meta: { touched, invalid, error },
    } = props;
    return <OptionGroupsControl isEditable={isEditable} {...input} />;
};
